import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import global from "@/global";
import { useSensitiveActionAuthorization } from "@/components/SensitiveActionAuthorization";
import { ExternalRegisterDetails } from "./ExternalRegisterDetails";
import type {
  ExternalRegisterDetailsResponse,
  ExternalRegisterDetails as ExternalRegisterDetailsRecord,
} from "./types";

type ExternalRegisterSecureDetailsModalProps = {
  open: boolean;
  patientId?: string | null;
  alertId?: string | null;
  isConciergeMode?: boolean;
  onClose: () => void;
};

type ClientResponse<T> = {
  data?: T;
};

const textOrFallback = (value: string, fallback: string): string => {
  return value.trim() || fallback;
};

export async function fetchExternalRegisterSecureDetails(
  patientId: string,
  actAsToken: string,
): Promise<ExternalRegisterDetailsResponse> {
  const response = await global.client.get(
    `patients/custom/${patientId}/external-register/details`,
    {
      headers: {
        "X-ACT-AS": actAsToken,
      },
    },
  ) as ClientResponse<ExternalRegisterDetailsResponse>;

  return response.data ?? {
    patientId,
    alerts: [],
  };
}

const ConciergeBlockedDialog: React.FC<ExternalRegisterSecureDetailsModalProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("externalRegister.secureDetails.title")}</DialogTitle>
      <DialogContent>
        <Alert severity="info">
          {t("externalRegister.secureDetails.conciergeBlocked")}
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common.buttons.close")}</Button>
      </DialogActions>
    </Dialog>
  );
};

const ExternalRegisterSecureDetailsModalContent: React.FC<Omit<ExternalRegisterSecureDetailsModalProps, "open" | "isConciergeMode">> = ({
  patientId,
  alertId,
  onClose,
}) => {
  const { t } = useTranslation();
  const { requestAuthorization, AuthorizationDialog } = useSensitiveActionAuthorization();
  const [selectedAlertId, setSelectedAlertId] = React.useState<string | null>(alertId ?? null);
  const [actAsToken, setActAsToken] = React.useState<string | null>(null);
  const [detailsResponse, setDetailsResponse] = React.useState<ExternalRegisterDetailsResponse | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const authorizationRequest = React.useMemo(() => ({
    reason: "external-register-secure-details",
    title: textOrFallback(t("externalRegister.secureDetails.authorization.title"), "External Register details require confirmation"),
    message: textOrFallback(t("externalRegister.secureDetails.authorization.message"), "Confirm clinician and operator code to view secure External Register details."),
    confirmLabel: textOrFallback(t("externalRegister.secureDetails.authorization.confirmLabel"), "View details"),
  }), [t]);

  const clearState = React.useCallback(() => {
    setSelectedAlertId(null);
    setActAsToken(null);
    setDetailsResponse(null);
    setLoading(false);
    setError(null);
  }, []);

  const handleClose = React.useCallback(() => {
    clearState();
    onClose();
  }, [clearState, onClose]);

  React.useEffect(() => {
    let cancelled = false;
    setSelectedAlertId(alertId ?? null);

    const authorizeAndFetch = async () => {
      if (!patientId) {
        setError(t("externalRegister.secureDetails.errors.patientIdRequired"));
        return;
      }

      setLoading(false);
      setError(null);
      setDetailsResponse(null);
      setActAsToken(null);

      const authorization = await requestAuthorization(authorizationRequest);
      if (cancelled) return;
      if (!authorization) {
        setError(t("externalRegister.secureDetails.errors.authorizationCancelled"));
        return;
      }

      setActAsToken(authorization.token);
      setLoading(true);
      try {
        const response = await fetchExternalRegisterSecureDetails(patientId, authorization.token);
        if (cancelled) return;
        setDetailsResponse(response);
      } catch (err: unknown) {
        if (cancelled) return;
        const message = (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
          ?? (err as Error)?.message
          ?? t("externalRegister.secureDetails.errors.loadFailed");
        setActAsToken(null);
        setDetailsResponse(null);
        setError(message);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void authorizeAndFetch();

    return () => {
      cancelled = true;
    };
  }, [alertId, authorizationRequest, patientId, requestAuthorization, t]);

  const visibleAlerts = React.useMemo<ExternalRegisterDetailsRecord[]>(() => {
    const alerts = detailsResponse?.alerts ?? [];
    if (!selectedAlertId) return alerts;
    return alerts.filter((alert) => alert.id === selectedAlertId);
  }, [detailsResponse?.alerts, selectedAlertId]);

  return (
    <>
      <Dialog open onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{t("externalRegister.secureDetails.title")}</DialogTitle>
        <DialogContent dividers>
          {!actAsToken && !loading && !detailsResponse && !error ? (
            <Typography variant="body2" color="text.secondary">
              {t("externalRegister.secureDetails.awaitingConfirmation")}
            </Typography>
          ) : null}
          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">{t("externalRegister.secureDetails.loading")}</Typography>
            </Box>
          ) : null}
          {error ? (
            <Alert severity="error">{error}</Alert>
          ) : null}
          {detailsResponse ? (
            <ExternalRegisterDetails alerts={visibleAlerts} />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t("common.buttons.close")}</Button>
        </DialogActions>
      </Dialog>
      <AuthorizationDialog />
    </>
  );
};

export const ExternalRegisterSecureDetailsModal: React.FC<ExternalRegisterSecureDetailsModalProps> = (props) => {
  if (!props.open) return null;
  if (props.isConciergeMode) {
    return <ConciergeBlockedDialog {...props} />;
  }

  return (
    <ExternalRegisterSecureDetailsModalContent
      patientId={props.patientId}
      alertId={props.alertId}
      onClose={props.onClose}
    />
  );
};

export default ExternalRegisterSecureDetailsModal;
